import { useEffect, useState } from 'react'
import { Sentry } from '@/lib/sentry'
import {
  esFalloReportable,
  fetchNovedadesAlEntrar,
  type NovedadesAlEntrar,
} from './novedades'

/**
 * Tres estados, no dos: mientras carga no se sabe si hay algo que contar, y
 * `falla` no es lo mismo que «no hay novedades». El aviso trata los dos igual
 * —no se monta—, pero la página propia sí los distingue: decir «aún no hay
 * novedades» cuando en realidad el servidor no contestó es mentirle al usuario.
 */
export type EstadoNovedades =
  | { estado: 'cargando' }
  | { estado: 'listo'; datos: NovedadesAlEntrar }
  | { estado: 'falla' }

/**
 * Pide las novedades del usuario logueado. **No bloquea el arranque**: corre en
 * un efecto (o sea, después del primer pintado) y nadie espera su resultado, así
 * que un endpoint lento o caído no deja al usuario mirando una pantalla vacía.
 *
 * Un servidor viejo responde 404 aquí —el módulo `novedades/` es del goal 1—, y
 * eso cae en `falla` como cualquier otro fallo: sin aviso y sin ruido.
 */
export function useNovedadesAlEntrar(): EstadoNovedades {
  const [estado, setEstado] = useState<EstadoNovedades>({ estado: 'cargando' })

  useEffect(() => {
    let activo = true
    fetchNovedadesAlEntrar()
      .then((datos) => {
        if (activo) setEstado({ estado: 'listo', datos })
      })
      .catch((error: unknown) => {
        // Tragarse el fallo es lo correcto de cara al usuario, pero callárselo
        // también a nosotros dejaría la pantalla desaparecer sin que nadie se
        // entere. `esFalloReportable` filtra lo esperado (404 del lockstep,
        // baches de red) para que solo llegue lo que sí es una incidencia.
        if (esFalloReportable(error)) {
          Sentry.captureException(error, {
            tags: { operation: 'GET /novedades/al-entrar' },
          })
        }
        if (activo) setEstado({ estado: 'falla' })
      })
    return () => {
      activo = false
    }
  }, [])

  return estado
}
